/** Mock session of the signed-in partner (persona «سارا محمدی», silver level) and her inbox. */
import type { OkResponse, PartnerMeResponse, PartnerNotificationView, PartnerNotificationsResponse, ReadAllNotificationsResponse } from "@mfa-aff/shared";
import { ApiError } from "../api/client";
import { contractPersonaActive } from "./contract-persona";

const DAY = 86_400_000;
const ago = (d: number, h = 0) => new Date(Date.now() - d * DAY - h * 3_600_000).toISOString();

export const meMock: PartnerMeResponse = {
    affiliate: {
        id: 1042,
        displayName: "سارا محمدی",
        firstName: "سارا",
        lastName: "محمدی",
        refCode: "SARA24",
        // `?mock_contract=1`: a contract (strategic) partner outside the ladder (mocks/contract-persona.ts).
        type: contractPersonaActive() ? "strategic" : "individual",
        status: "active",
        kycStatus: "pending",
        nationalCodeMasked: "007****042",
        levelLocked: contractPersonaActive(),
        level: { id: 2, rank: 2, name: "نقره‌ای" },
        whmcsClientId: 18342,
        mobile: "0912***4567",
        email: "sara.mohammadi@example.com",
        birthYear: 1372,
        hasSheba: false,
        joinedAt: ago(520),
    },
    user: { id: "7f3c2a9e-1042-4c6b-9d1e-5a8b2f0c1042", fullName: "سارا محمدی", mobile: "0912***4567", role: "owner" },
    session: { method: "oidc" },
    rules: {
        acceptedVersion: "3",
        acceptedAt: ago(118),
        currentVersion: "3",
        needsAcceptance: false,
        mustAccept: false,
        currentChangeSummary: ["- برداشت به کیف پول ۵٪ هدیه می‌گیرد.", "- تسویهٔ خودکار با آستانهٔ مبلغ یا روزهای ماه اضافه شد."].join("\n"),
        // Same as mocks/rules.ts `announced` (version 4, in 27 days).
        upcoming: {
            version: "4",
            effectiveFrom: ago(-27),
            effectiveFromJalali: new Intl.DateTimeFormat("fa-IR-u-ca-persian", { year: "numeric", month: "long", day: "numeric", timeZone: "Asia/Tehran" }).format(new Date(Date.now() + 27 * DAY)),
            changeSummary: ["- نرخ تمدید سطح نقره‌ای از ۸٪ به ۹٪ افزایش می‌یابد.", "- بند «رفتارهای ممنوع» با مثال‌های روشن‌تر بازنویسی شد."].join("\n"),
            accepted: false,
        },
    },
    features: { ambassador: true, payoutsEnabled: true, channelUrl: "https://t.me/mizbanfa" },
    onboarding: {
        profileDone: true,
        refLinkDone: true,
        firstShareDone: false,
        firstCodeDone: true,
        firstSaleDone: false,
        firstApprovedDone: false,
        shebaDone: false,
        payoutDone: false,
        dismissed: false,
        firstSaleBonusRial: "2000000",
    },
    // `?mock_impersonation=1` fills this in (mocks/admin-view.ts).
    impersonation: null,
};

/** Newest first; ids are numeric strings like the API's bigint ids. */
const notifications: PartnerNotificationView[] = [
    {
        id: "110",
        type: "feedback.replied",
        title: "پاسخ تیم میزبان‌فا به پیشنهاد شما",
        body: "ممنون از پیشنهادتان. بنرهای استوری اضافه شد؛ در کیت بازاریابی ببینید.\n\nپیام شما: «کاش بنرهای کیت بازاریابی اندازهٔ استوری اینستاگرام هم داشتند.»",
        data: { feedbackId: 9, kind: "suggestion" },
        readAt: null,
        createdAt: ago(0, 1),
    },
    {
        id: "109",
        type: "commission.approved",
        title: "پورسانت تأیید شد",
        body: "پورسانت سرور مجازی ابری (no***.com) به مبلغ ۱٬۳۰۰٬۰۰۰ تومان قابل برداشت شد.",
        data: { amountRial: "13000000", count: 1 },
        readAt: null,
        createdAt: ago(0, 3),
    },
    {
        id: "108",
        type: "payout.settled",
        title: "تسویه انجام شد",
        body: "۸٬۲۰۰٬۰۰۰ تومان به کیف پول میزبان‌فا واریز شد؛ ۵٪ هدیه هم اضافه شد.",
        data: { amountRial: "82000000", payoutId: "5521" },
        readAt: null,
        createdAt: ago(1, 5),
    },
    {
        id: "107",
        type: "level.grace",
        title: "یک فصل مهلت برای حفظ سطح",
        body: "در بازبینی فصلی، شرط سطح نقره‌ای برقرار نبود. تا بازبینی بعدی فرصت دارید دوباره به آن برسید.",
        data: { levelId: 2 },
        readAt: null,
        createdAt: ago(3),
    },
    {
        id: "106",
        type: "commission.reversed",
        title: "پورسانت برگشت خورد",
        body: "مشتری هاست وردپرس حرفه‌ای (ar***.ir) در دورهٔ ضمانت انصراف داد.",
        data: { amountRial: "8500000" },
        readAt: ago(5),
        createdAt: ago(6),
    },
    {
        id: "105",
        type: "payout.failed",
        title: "واریز بانکی برگشت خورد",
        body: "واریز ۲٬۴۰۰٬۰۰۰ تومان به شبای شما انجام نشد و مبلغ به موجودی قابل برداشت برگشت. شبا را بررسی کنید.",
        data: { amountRial: "24000000", payoutId: "5490" },
        readAt: ago(8),
        createdAt: ago(9),
    },
    {
        id: "104",
        type: "commission.approved",
        title: "پورسانت تأیید شد",
        body: "۳ پورسانت به مبلغ ۲٬۱۸۰٬۰۰۰ تومان قابل برداشت شد.",
        data: { amountRial: "21800000", count: 3 },
        readAt: ago(39),
        createdAt: ago(40),
    },
];

/** Adds a new unread inbox row on top (mock writes that notify, e.g. «تغییر قرارداد»). */
export function pushNotificationMock(n: Omit<PartnerNotificationView, "id" | "readAt" | "createdAt">): void {
    const id = String(Math.max(0, ...notifications.map((x) => Number(x.id))) + 1);
    notifications.unshift({ ...n, id, readAt: null, createdAt: new Date().toISOString() });
}

export function listNotificationsMock(before: string | undefined, limit: number): PartnerNotificationsResponse {
    const rows = notifications.filter((n) => before === undefined || Number(n.id) < Number(before)).slice(0, limit);
    return { items: structuredClone(rows), unread: notifications.filter((n) => n.readAt === null).length };
}

export function markNotificationReadMock(id: string): OkResponse {
    const n = notifications.find((x) => x.id === id);
    if (!n) throw new ApiError(404, "این اعلان پیدا نشد.");
    n.readAt ??= new Date().toISOString();
    return { ok: true };
}

export function markAllNotificationsReadMock(): ReadAllNotificationsResponse {
    const now = new Date().toISOString();
    let updated = 0;
    for (const n of notifications) {
        if (n.readAt === null) {
            n.readAt = now;
            updated++;
        }
    }
    return { updated };
}
